import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useQuery } from '../lib/useQuery.js';
import { useAuth } from '../lib/auth.js';
import { fileSize } from '../lib/format.js';
import { ancestry, childrenByParent, isWithin } from '../lib/folderTree.js';
import { Page } from '../app/AppShell.js';
import { Modal } from '../components/Modal.js';
import { RowMenu } from '../components/RowMenu.js';
import { ShareWithDemoAdmins } from '../components/ShareWithDemoAdmins.js';
import { useToast } from '../components/Toast.js';
import { Loading, ErrorNote, EmptyState } from '../components/ui.js';
import type { MediaFolder, MediaItem, MediaScope, MediaSort } from '../lib/types.js';

/**
 * The media library as a file explorer: a folder tree, breadcrumbs, and a
 * pane listing the current folder's subfolders and files. Files and folders
 * move by drag-and-drop (or "Move to…"); files dropped from the desktop upload
 * into the folder being viewed. The location lives in `?f=` so back/forward
 * and links work. The folder list comes from `/api/media/folders`, which only
 * needs `media.view`; creating/renaming/moving folders still needs
 * `folders.manage` (ADR-0024).
 */

type Location = { kind: 'root' } | { kind: 'all' } | { kind: 'folder'; id: string };
type Dragged = { kind: 'media'; ids: string[] } | { kind: 'folder'; id: string };
type ViewMode = 'grid' | 'list';
type SortKey = MediaSort;

const VIEW_KEY = 'akarya.media.view';
const MEDIA_MIME = 'application/x-akarya-media';
const FOLDER_MIME = 'application/x-akarya-folder';
/** Drop-target key for the library root (unfiled). */
const ROOT = '';

function parseLocation(f: string | null): Location {
  if (!f) return { kind: 'root' };
  if (f === 'all') return { kind: 'all' };
  return { kind: 'folder', id: f };
}

function readView(): ViewMode {
  try {
    return localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'grid';
  } catch {
    return 'grid';
  }
}

function mediaName(m: MediaItem): string {
  return m.originalName ?? m.sha256.slice(0, 12);
}

/** Files fetched per page; more arrive as the pane scrolls. */
const PAGE_SIZE = 120;
/** The server's cap on one page, which a refetch of everything loaded so far is bound by. */
const MAX_PAGE = 500;

function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    // Clearing the search applies at once; typing waits for a pause.
    if (value === '') {
      setSettled(value);
      return;
    }
    const id = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return settled;
}

/**
 * The library a page at a time, searched and sorted on the server, so a big
 * library never arrives (or renders) in one go. While a new search or sort
 * loads, the previous results stay visible (the pane also filters them on
 * the client), but a new location never shows the old location's files.
 * `refetch` (after an upload, move or delete) reloads everything loaded so
 * far, so the pane doesn't shrink back to one page.
 */
function useMediaPages(
  scope: MediaScope,
  sort: MediaSort,
  q: string,
): {
  items: MediaItem[] | undefined;
  hasMore: boolean;
  loading: boolean;
  error: Error | undefined;
  loadMore: () => void;
  refetch: () => void;
} {
  const scopeKey = scope.kind === 'folder' ? `folder:${scope.id}` : scope.kind;
  const key = `${scopeKey}\n${sort}\n${q}`;
  const [state, setState] = useState<{ scopeKey: string; key: string; items: MediaItem[]; hasMore: boolean }>();
  const [error, setError] = useState<Error>();
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const run = useRef(0);
  const current = { scope, sort, q, scopeKey, key, loaded: state?.key === key ? state.items.length : 0 };
  const latest = useRef(current);
  latest.current = current;
  const reloadAll = useRef(false);

  function fetchFrom(offset: number, limit: number, append: boolean): void {
    const id = ++run.current;
    const { scope, sort, q, scopeKey, key } = latest.current;
    setLoading(true);
    api
      .mediaPage(scope, { offset, limit, sort, q })
      .then((page) => {
        if (id !== run.current) return;
        setState((prev) => ({
          scopeKey,
          key,
          items: append && prev?.key === key ? [...prev.items, ...page.data] : page.data,
          hasMore: page.hasMore,
        }));
        setError(undefined);
      })
      .catch((err: unknown) => {
        if (id === run.current) setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (id === run.current) setLoading(false);
      });
  }

  useEffect(() => {
    const limit = reloadAll.current ? Math.min(MAX_PAGE, Math.max(PAGE_SIZE, latest.current.loaded)) : PAGE_SIZE;
    reloadAll.current = false;
    fetchFrom(0, limit, false);
  }, [key, tick]);

  const fresh = state?.key === key;
  const refetch = useCallback(() => {
    reloadAll.current = true;
    setTick((n) => n + 1);
  }, []);

  return {
    items: state?.scopeKey === scopeKey ? state.items : undefined,
    hasMore: fresh && state.hasMore,
    loading,
    error,
    loadMore: () => {
      if (!loading && fresh && state.hasMore) fetchFrom(state.items.length, PAGE_SIZE, true);
    },
    refetch,
  };
}

export function MediaLibraryScreen(): ReactNode {
  const { can } = useAuth();
  const canDelete = can('media.manage');
  const canFolders = can('folders.manage');
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const location = parseLocation(params.get('f'));
  const locationKey = location.kind === 'folder' ? location.id : location.kind;
  const currentFolderId = location.kind === 'folder' ? location.id : null;

  const folders = useQuery(() => api.mediaFolders(), []);

  const [view, setView] = useState<ViewMode>(readView);
  const [sort, setSort] = useState<SortKey>('newest');
  const [search, setSearch] = useState('');
  const serverQuery = useDebounced(search.trim().toLowerCase(), 250);
  const media = useMediaPages(
    location.kind === 'all' ? { kind: 'all' } : location.kind === 'root' ? { kind: 'unfiled' } : location,
    sort,
    serverQuery,
  );
  const items = media.items;
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const anchorRef = useRef<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [dragged, setDragged] = useState<Dragged | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [fileDragOver, setFileDragOver] = useState(false);
  const [uploading, setUploading] = useState(0);
  // Bytes across every file queued since the last idle moment, so overlapping drops share one bar.
  const uploadBatch = useRef({ total: 0, done: 0, inFlight: new Map<File, number>() });
  const [uploadPercent, setUploadPercent] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const [preview, setPreview] = useState<MediaItem | null>(null);
  // Admins share items with demo administrators, who otherwise only see what they made.
  const canShare = can('users.manage');
  const [sharing, setSharing] = useState<{ id: string; name: string } | null>(null);
  const [moving, setMoving] = useState<{ kind: 'media'; ids: string[] } | { kind: 'folder'; folder: MediaFolder } | null>(null);
  const [naming, setNaming] = useState<{ mode: 'create'; parentId: string | null } | { mode: 'rename'; folder: MediaFolder } | null>(null);

  const allFolders = folders.data ?? [];
  const byParent = useMemo(() => childrenByParent(allFolders), [allFolders]);
  const crumbs = currentFolderId ? ancestry(allFolders, currentFolderId) : [];
  const currentFolder = crumbs.at(-1);

  // A new location starts with nothing selected and no filter.
  useEffect(() => {
    setSelected(new Set());
    anchorRef.current = null;
    setSearch('');
  }, [locationKey]);

  // Keep the tree opened down to wherever we are.
  useEffect(() => {
    if (crumbs.length < 2) return;
    setExpanded((prev) => {
      const missing = crumbs.slice(0, -1).filter((f) => !prev.has(f.id));
      if (missing.length === 0) return prev;
      const next = new Set(prev);
      for (const f of missing) next.add(f.id);
      return next;
    });
  }, [crumbs.map((f) => f.id).join('/')]);

  // A folder that vanished (deleted, or access revoked) falls back to the root.
  useEffect(() => {
    if (currentFolderId && folders.data && !currentFolder) setParams({}, { replace: true });
  }, [currentFolderId, folders.data, currentFolder]);

  function go(next: Location): void {
    setParams(next.kind === 'root' ? {} : { f: next.kind === 'all' ? 'all' : next.id });
  }

  function changeView(v: ViewMode): void {
    setView(v);
    try {
      localStorage.setItem(VIEW_KEY, v);
    } catch {
      // per-viewer convenience only
    }
  }

  // ---- what the pane shows ---------------------------------------------------

  const needle = search.trim().toLowerCase();
  const subfolders =
    location.kind === 'all'
      ? []
      : (byParent.get(currentFolderId) ?? []).filter((f) => !needle || f.name.toLowerCase().includes(needle));

  const files = useMemo(() => {
    const list = (items ?? []).filter(
      (m) =>
        !needle ||
        (m.originalName ?? '').toLowerCase().includes(needle) ||
        (m.mime ?? '').toLowerCase().includes(needle) ||
        m.sha256.startsWith(needle),
    );
    if (sort === 'name') return [...list].sort((a, b) => mediaName(a).localeCompare(mediaName(b)));
    if (sort === 'size') return [...list].sort((a, b) => b.size - a.size);
    return list; // the API already returns newest first
  }, [items, needle, sort]);

  const selectedItems = files.filter((m) => selected.has(m.id));
  const folderName = (id: string | null): string =>
    id === null ? 'Library' : (allFolders.find((f) => f.id === id)?.name ?? '…');

  // ---- selection ---------------------------------------------------------------

  function clickItem(e: MouseEvent, item: MediaItem): void {
    if (e.shiftKey && anchorRef.current) {
      const from = files.findIndex((m) => m.id === anchorRef.current);
      const to = files.findIndex((m) => m.id === item.id);
      if (from >= 0 && to >= 0) {
        const [lo, hi] = from < to ? [from, to] : [to, from];
        setSelected(new Set(files.slice(lo, hi + 1).map((m) => m.id)));
        return;
      }
    }
    anchorRef.current = item.id;
    if (e.ctrlKey || e.metaKey) toggle(item.id);
    else setSelected(new Set([item.id]));
  }

  function toggle(id: string): void {
    anchorRef.current = id;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function onPaneKey(e: KeyboardEvent): void {
    if ((e.target as HTMLElement).closest('input, select, textarea')) return;
    if (e.key === 'Escape') setSelected(new Set());
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') {
      e.preventDefault();
      setSelected(new Set(files.map((m) => m.id)));
    }
    if (e.key === 'Delete' && canDelete && selectedItems.length > 0) void removeMedia(selectedItems);
  }

  // ---- mutations -----------------------------------------------------------------

  async function upload(list: File[]): Promise<void> {
    if (list.length === 0) return;
    const batch = uploadBatch.current;
    if (batch.inFlight.size === 0 && batch.done >= batch.total) {
      batch.total = 0;
      batch.done = 0;
      setUploadPercent(0);
    }
    batch.total += list.reduce((sum, f) => sum + f.size, 0);
    const report = (): void => {
      let sent = batch.done;
      for (const bytes of batch.inFlight.values()) sent += bytes;
      setUploadPercent(batch.total > 0 ? Math.min(100, Math.round((sent / batch.total) * 100)) : 100);
    };

    setUploading((n) => n + list.length);
    let uploaded = 0;
    for (const file of list) {
      batch.inFlight.set(file, 0);
      try {
        // Scale the reported fraction onto the file's own size for the batch total.
        await api.uploadMedia(file, currentFolderId, (loaded, total) => {
          batch.inFlight.set(file, total > 0 ? (loaded / total) * file.size : 0);
          report();
        });
        uploaded++;
      } catch (err) {
        toast.error(err);
      } finally {
        batch.inFlight.delete(file);
        batch.done += file.size;
        report();
        setUploading((n) => n - 1);
      }
    }
    if (uploaded > 0) {
      toast.push(`Uploaded ${uploaded} file${uploaded === 1 ? '' : 's'} to ${currentFolder?.name ?? 'Library'}`, 'success');
      refresh();
    }
  }

  function refresh(): void {
    media.refetch();
    folders.refetch();
  }

  async function moveMedia(ids: string[], target: string | null): Promise<void> {
    const toMove = (items ?? []).filter((m) => ids.includes(m.id) && m.folderId !== target);
    if (toMove.length === 0) return;
    let moved = 0;
    for (const item of toMove) {
      try {
        await api.moveMedia(item.id, target);
        moved++;
      } catch (err) {
        toast.error(err);
      }
    }
    if (moved > 0) {
      toast.push(`Moved ${moved} file${moved === 1 ? '' : 's'} to ${folderName(target)}`, 'success');
      setSelected(new Set());
      refresh();
    }
  }

  async function moveFolder(folder: MediaFolder, target: string | null): Promise<void> {
    if (folder.parentId === target || folder.id === target) return;
    if (target !== null && isWithin(allFolders, target, folder.id)) {
      toast.push('A folder cannot be moved into itself.', 'error');
      return;
    }
    try {
      await api.moveFolder(folder.id, target);
      toast.push(`Moved “${folder.name}” to ${folderName(target)}`, 'success');
      folders.refetch();
    } catch (err) {
      toast.error(err);
    }
  }

  async function removeMedia(items: MediaItem[]): Promise<void> {
    const what = items.length === 1 ? `“${mediaName(items[0]!)}”` : `${items.length} files`;
    if (!confirm(`Delete ${what}? This cannot be undone.`)) return;
    let deleted = 0;
    for (const item of items) {
      try {
        await api.deleteMedia(item.id);
        deleted++;
      } catch (err) {
        toast.error(err);
      }
    }
    if (deleted > 0) {
      toast.push(`Deleted ${deleted} file${deleted === 1 ? '' : 's'}`, 'success');
      setSelected(new Set());
      refresh();
    }
  }

  async function removeFolder(folder: MediaFolder): Promise<void> {
    if (!confirm(`Delete folder “${folder.name}”? It must be empty.`)) return;
    try {
      await api.deleteFolder(folder.id);
      toast.push('Folder deleted', 'success');
      if (currentFolderId && isWithin(allFolders, currentFolderId, folder.id)) go(folder.parentId ? { kind: 'folder', id: folder.parentId } : { kind: 'root' });
      folders.refetch();
    } catch (err) {
      toast.error(err);
    }
  }

  // ---- drag and drop -------------------------------------------------------------

  function startMediaDrag(e: DragEvent, item: MediaItem): void {
    const ids = selected.has(item.id) ? [...selected] : [item.id];
    if (!selected.has(item.id)) setSelected(new Set([item.id]));
    e.dataTransfer.setData(MEDIA_MIME, ids.join(','));
    e.dataTransfer.effectAllowed = 'move';
    setDragged({ kind: 'media', ids });
  }

  function startFolderDrag(e: DragEvent, folder: MediaFolder): void {
    e.dataTransfer.setData(FOLDER_MIME, folder.id);
    e.dataTransfer.effectAllowed = 'move';
    setDragged({ kind: 'folder', id: folder.id });
  }

  function endDrag(): void {
    setDragged(null);
    setDropTarget(null);
  }

  function accepts(target: string | null): boolean {
    if (!dragged) return false;
    if (dragged.kind === 'media') return true;
    if (!canFolders) return false;
    const folder = allFolders.find((f) => f.id === dragged.id);
    if (!folder || folder.parentId === target) return false;
    return target === null || !isWithin(allFolders, target, folder.id);
  }

  /** Props that make an element a drop target for dragged files/folders. */
  function dropZone(target: string | null): {
    onDragOver: (e: DragEvent) => void;
    onDragLeave: (e: DragEvent) => void;
    onDrop: (e: DragEvent) => void;
    'data-drop': boolean;
  } {
    const key = target ?? ROOT;
    return {
      'data-drop': dropTarget === key,
      onDragOver: (e) => {
        if (!accepts(target)) return;
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'move';
        if (dropTarget !== key) setDropTarget(key);
      },
      onDragLeave: (e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
        if (dropTarget === key) setDropTarget(null);
      },
      onDrop: (e) => {
        if (!dragged || !accepts(target)) return;
        e.preventDefault();
        e.stopPropagation();
        const d = dragged;
        endDrag();
        if (d.kind === 'media') void moveMedia(d.ids, target);
        else {
          const folder = allFolders.find((f) => f.id === d.id);
          if (folder) void moveFolder(folder, target);
        }
      },
    };
  }

  // Desktop files dropped anywhere on the pane upload into the current folder.
  const paneFileDrop = {
    onDragOver: (e: DragEvent) => {
      if (!e.dataTransfer.types.includes('Files')) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      setFileDragOver(true);
    },
    onDragLeave: (e: DragEvent) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFileDragOver(false);
    },
    onDrop: (e: DragEvent) => {
      if (!e.dataTransfer.types.includes('Files')) return;
      e.preventDefault();
      setFileDragOver(false);
      void upload(Array.from(e.dataTransfer.files));
    },
  };

  // ---- rendering ---------------------------------------------------------------------

  function folderMenu(f: MediaFolder): ReactNode {
    return (
      <RowMenu
        label={`Actions for ${f.name}`}
        items={[
          { label: 'Open', onSelect: () => go({ kind: 'folder', id: f.id }) },
          canFolders && { label: 'New subfolder…', onSelect: () => setNaming({ mode: 'create', parentId: f.id }) },
          canFolders && { label: 'Rename…', onSelect: () => setNaming({ mode: 'rename', folder: f }) },
          canFolders && { label: 'Move to…', onSelect: () => setMoving({ kind: 'folder', folder: f }) },
          canFolders && { label: 'Delete', danger: true, onSelect: () => void removeFolder(f) },
        ]}
      />
    );
  }

  function mediaMenu(m: MediaItem): ReactNode {
    return (
      <RowMenu
        label={`Actions for ${mediaName(m)}`}
        items={[
          { label: 'Preview', onSelect: () => setPreview(m) },
          canShare && { label: 'Share with demo admins…', onSelect: () => setSharing({ id: m.id, name: mediaName(m) }) },
          {
            label: 'Copy digest',
            onSelect: () => void navigator.clipboard?.writeText(m.sha256).then(() => toast.push('Digest copied')),
          },
          {
            label: selected.has(m.id) && selected.size > 1 ? `Move ${selected.size} files to…` : 'Move to…',
            onSelect: () => setMoving({ kind: 'media', ids: selected.has(m.id) ? [...selected] : [m.id] }),
          },
          canDelete && {
            label: selected.has(m.id) && selected.size > 1 ? `Delete ${selected.size} files` : 'Delete',
            danger: true,
            onSelect: () => void removeMedia(selected.has(m.id) ? selectedItems : [m]),
          },
        ]}
      />
    );
  }

  function renderTree(parentId: string | null, depth: number): ReactNode {
    return (byParent.get(parentId) ?? []).map((f) => {
      const open = expanded.has(f.id);
      const hasChildren = byParent.has(f.id);
      return (
        <li key={f.id}>
          <div
            className={`explorer-node${currentFolderId === f.id ? ' active' : ''}`}
            style={{ paddingLeft: 6 + depth * 14 }}
            draggable={canFolders}
            onDragStart={(e) => startFolderDrag(e, f)}
            onDragEnd={endDrag}
            {...dropZone(f.id)}
          >
            <button
              type="button"
              className="explorer-twisty"
              aria-label={open ? `Collapse ${f.name}` : `Expand ${f.name}`}
              style={{ visibility: hasChildren ? 'visible' : 'hidden' }}
              onClick={() =>
                setExpanded((prev) => {
                  const next = new Set(prev);
                  if (open) next.delete(f.id);
                  else next.add(f.id);
                  return next;
                })
              }
            >
              {open ? '▾' : '▸'}
            </button>
            <button type="button" className="explorer-node-label" onClick={() => go({ kind: 'folder', id: f.id })}>
              <FolderIcon open={currentFolderId === f.id} />
              <span className="ellipsis">{f.name}</span>
              {f.mediaCount > 0 ? <span className="faint small">{f.mediaCount}</span> : null}
            </button>
          </div>
          {open && hasChildren ? <ul>{renderTree(f.id, depth + 1)}</ul> : null}
        </li>
      );
    });
  }

  const busyLoading = (media.loading && !items) || (folders.loading && !folders.data);
  const empty = items && files.length === 0 && subfolders.length === 0;
  const totalSize = files.reduce((n, m) => n + m.size, 0);

  return (
    <Page
      title="Media library"
      actions={
        <div className="row">
          {canFolders ? (
            <button className="btn" onClick={() => setNaming({ mode: 'create', parentId: currentFolderId })}>
              New folder
            </button>
          ) : null}
          <button className="btn primary" onClick={() => inputRef.current?.click()}>
            Upload
          </button>
        </div>
      }
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*,video/*"
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files) void upload(Array.from(e.target.files));
          e.target.value = '';
        }}
      />

      <div className="explorer">
        <nav className="card explorer-tree" aria-label="Folders">
          <ul>
            <li>
              <div className={`explorer-node${location.kind === 'all' ? ' active' : ''}`}>
                <span className="explorer-twisty" />
                <button type="button" className="explorer-node-label" onClick={() => go({ kind: 'all' })}>
                  <StackIcon />
                  <span className="ellipsis">All media</span>
                </button>
              </div>
            </li>
            <li>
              <div className={`explorer-node${location.kind === 'root' ? ' active' : ''}`} {...dropZone(null)}>
                <span className="explorer-twisty" />
                <button type="button" className="explorer-node-label" onClick={() => go({ kind: 'root' })}>
                  <HomeIcon />
                  <span className="ellipsis">Library</span>
                </button>
              </div>
              <ul>{renderTree(null, 1)}</ul>
            </li>
          </ul>
          {folders.error ? <div className="form-error small card-pad">{folders.error.message}</div> : null}
        </nav>

        <section
          className={`card explorer-pane${fileDragOver ? ' file-over' : ''}`}
          tabIndex={-1}
          onKeyDown={onPaneKey}
          {...paneFileDrop}
        >
          <div className="explorer-toolbar">
            <ol className="explorer-crumbs" aria-label="Location">
              {location.kind === 'all' ? (
                <li>
                  <span>All media</span>
                </li>
              ) : (
                <>
                  <li>
                    <button type="button" className="crumb" {...dropZone(null)} onClick={() => go({ kind: 'root' })}>
                      Library
                    </button>
                  </li>
                  {crumbs.map((f, i) => (
                    <li key={f.id}>
                      {i === crumbs.length - 1 ? (
                        <span aria-current="page">{f.name}</span>
                      ) : (
                        <button
                          type="button"
                          className="crumb"
                          {...dropZone(f.id)}
                          onClick={() => go({ kind: 'folder', id: f.id })}
                        >
                          {f.name}
                        </button>
                      )}
                    </li>
                  ))}
                </>
              )}
            </ol>
            <div className="row explorer-tools">
              <input
                type="search"
                placeholder={location.kind === 'all' ? 'Search all media' : 'Search this folder'}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search"
              />
              <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)} aria-label="Sort by">
                <option value="newest">Newest</option>
                <option value="name">Name</option>
                <option value="size">Largest</option>
              </select>
              <div className="segmented" role="group" aria-label="View">
                <button type="button" aria-pressed={view === 'grid'} onClick={() => changeView('grid')}>
                  Grid
                </button>
                <button type="button" aria-pressed={view === 'list'} onClick={() => changeView('list')}>
                  List
                </button>
              </div>
            </div>
          </div>

          {selected.size > 0 ? (
            <div className="explorer-selection">
              <span>
                <strong>{selected.size}</strong> selected
              </span>
              <button className="btn small" onClick={() => setMoving({ kind: 'media', ids: [...selected] })}>
                Move to…
              </button>
              {canDelete ? (
                <button className="btn small danger" onClick={() => void removeMedia(selectedItems)}>
                  Delete
                </button>
              ) : null}
              <button className="btn small ghost" onClick={() => setSelected(new Set())}>
                Clear
              </button>
            </div>
          ) : null}

          <div className="explorer-body">
            {busyLoading ? <Loading what="media" /> : null}
            {media.error ? <ErrorNote error={media.error} /> : null}

            {!busyLoading && empty ? (
              <EmptyState>
                {needle
                  ? `Nothing here matches “${search.trim()}”.`
                  : location.kind === 'folder'
                    ? 'This folder is empty. Drop files here to upload them into it.'
                    : 'Nothing in the library yet. Drop files here, or use Upload.'}
              </EmptyState>
            ) : null}

            {!busyLoading && !empty && view === 'grid' ? (
              <div className="explorer-grid">
                {subfolders.map((f) => (
                  <div
                    key={f.id}
                    className="explorer-tile folder"
                    draggable={canFolders}
                    onDragStart={(e) => startFolderDrag(e, f)}
                    onDragEnd={endDrag}
                    onDoubleClick={() => go({ kind: 'folder', id: f.id })}
                    {...dropZone(f.id)}
                  >
                    <button type="button" className="explorer-tile-main" onClick={() => go({ kind: 'folder', id: f.id })}>
                      <div className="explorer-thumb folder-thumb">
                        <FolderIcon size={44} />
                      </div>
                      <div className="explorer-tile-meta">
                        <div className="ellipsis explorer-tile-name">{f.name}</div>
                        <div className="faint small">{folderSummary(f)}</div>
                      </div>
                    </button>
                    <div className="explorer-tile-menu">{folderMenu(f)}</div>
                  </div>
                ))}
                {files.map((m) => (
                  <div
                    key={m.id}
                    className={`explorer-tile${selected.has(m.id) ? ' selected' : ''}${dragged?.kind === 'media' && dragged.ids.includes(m.id) ? ' dragging' : ''}`}
                    draggable
                    onDragStart={(e) => startMediaDrag(e, m)}
                    onDragEnd={endDrag}
                  >
                    <button
                      type="button"
                      className="explorer-tile-main"
                      aria-pressed={selected.has(m.id)}
                      onClick={(e) => clickItem(e, m)}
                      onDoubleClick={() => setPreview(m)}
                    >
                      <MediaThumb item={m} />
                      <div className="explorer-tile-meta">
                        <div className="ellipsis explorer-tile-name" title={mediaName(m)}>
                          {m.originalName ?? '(unnamed)'}
                        </div>
                        <div className="faint small">
                          {fileSize(m.size)}
                          {m.width && m.height ? ` · ${m.width}×${m.height}` : ''}
                        </div>
                      </div>
                    </button>
                    <input
                      type="checkbox"
                      className="explorer-check"
                      aria-label={`Select ${mediaName(m)}`}
                      checked={selected.has(m.id)}
                      onChange={() => toggle(m.id)}
                    />
                    <div className="explorer-tile-menu">{mediaMenu(m)}</div>
                  </div>
                ))}
              </div>
            ) : null}

            {!busyLoading && !empty && view === 'list' ? (
              <table className="grid explorer-list">
                <thead>
                  <tr>
                    <th style={{ width: 32 }}>
                      <input
                        type="checkbox"
                        aria-label="Select all"
                        checked={files.length > 0 && selectedItems.length === files.length}
                        onChange={(e) => setSelected(e.target.checked ? new Set(files.map((m) => m.id)) : new Set())}
                      />
                    </th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Size</th>
                    <th>Dimensions</th>
                    {location.kind === 'all' ? <th>Folder</th> : null}
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {subfolders.map((f) => (
                    <tr
                      key={f.id}
                      className="explorer-row"
                      draggable={canFolders}
                      onDragStart={(e) => startFolderDrag(e, f)}
                      onDragEnd={endDrag}
                      onDoubleClick={() => go({ kind: 'folder', id: f.id })}
                      {...dropZone(f.id)}
                    >
                      <td />
                      <td>
                        <button type="button" className="explorer-row-name" onClick={() => go({ kind: 'folder', id: f.id })}>
                          <FolderIcon />
                          <span className="ellipsis">{f.name}</span>
                        </button>
                      </td>
                      <td className="muted small">Folder</td>
                      <td className="muted small">{folderSummary(f)}</td>
                      <td />
                      <td>{folderMenu(f)}</td>
                    </tr>
                  ))}
                  {files.map((m) => (
                    <tr
                      key={m.id}
                      className={`explorer-row${selected.has(m.id) ? ' selected' : ''}`}
                      draggable
                      onDragStart={(e) => startMediaDrag(e, m)}
                      onDragEnd={endDrag}
                      onClick={(e) => {
                        if (!(e.target as HTMLElement).closest('button, input')) clickItem(e, m);
                      }}
                      onDoubleClick={() => setPreview(m)}
                    >
                      <td>
                        <input
                          type="checkbox"
                          aria-label={`Select ${mediaName(m)}`}
                          checked={selected.has(m.id)}
                          onChange={() => toggle(m.id)}
                        />
                      </td>
                      <td>
                        <button type="button" className="explorer-row-name" onClick={() => setPreview(m)}>
                          <MediaThumb item={m} small />
                          <span className="ellipsis">{m.originalName ?? '(unnamed)'}</span>
                        </button>
                      </td>
                      <td className="muted small mono">{m.mime ?? '—'}</td>
                      <td className="muted small">{fileSize(m.size)}</td>
                      <td className="muted small">{m.width && m.height ? `${m.width}×${m.height}` : '—'}</td>
                      {location.kind === 'all' ? (
                        <td className="muted small">
                          {m.folderId ? (
                            <button type="button" className="crumb" onClick={() => go({ kind: 'folder', id: m.folderId! })}>
                              {folderName(m.folderId)}
                            </button>
                          ) : (
                            'Library'
                          )}
                        </td>
                      ) : null}
                      <td>{mediaMenu(m)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}

            {!busyLoading && media.hasMore ? <LoadMore loading={media.loading} onLoad={media.loadMore} /> : null}
          </div>

          {uploading > 0 ? (
            <div
              className="upload-progress"
              role="progressbar"
              aria-label="Upload progress"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={uploadPercent}
            >
              <div className="upload-progress-fill" style={{ width: `${uploadPercent}%` }} />
            </div>
          ) : null}
          <footer className="explorer-status faint small">
            <span>
              {subfolders.length > 0 ? `${subfolders.length} folder${subfolders.length === 1 ? '' : 's'} · ` : ''}
              {files.length}
              {media.hasMore ? '+' : ''} file{files.length === 1 && !media.hasMore ? '' : 's'} · {fileSize(totalSize)}
              {media.hasMore ? ' loaded' : ''}
            </span>
            <span>
              {uploading > 0
                ? `Uploading ${uploading} file${uploading === 1 ? '' : 's'}… ${uploadPercent}%`
                : location.kind === 'all'
                  ? 'Drop files to upload to Library'
                  : `Drop files to upload to ${currentFolder?.name ?? 'Library'}`}
            </span>
          </footer>

          {fileDragOver ? (
            <div className="explorer-drop-overlay">Drop to upload to {currentFolder?.name ?? 'Library'}</div>
          ) : null}
        </section>
      </div>

      {preview ? (
        <PreviewModal
          item={preview}
          folder={folderName(preview.folderId)}
          onClose={() => setPreview(null)}
          onCopy={() => toast.push('Digest copied')}
        />
      ) : null}

      {moving ? (
        <MoveModal
          title={
            moving.kind === 'folder'
              ? `Move “${moving.folder.name}”`
              : moving.ids.length === 1
                ? `Move “${mediaName((items ?? []).find((m) => m.id === moving.ids[0]) ?? files[0]!)}”`
                : `Move ${moving.ids.length} files`
          }
          folders={allFolders}
          exclude={moving.kind === 'folder' ? moving.folder.id : undefined}
          initial={moving.kind === 'folder' ? moving.folder.parentId : currentFolderId}
          onClose={() => setMoving(null)}
          onMove={(target) => {
            const m = moving;
            setMoving(null);
            if (m.kind === 'folder') void moveFolder(m.folder, target);
            else void moveMedia(m.ids, target);
          }}
        />
      ) : null}

      {naming ? (
        <NameModal
          title={
            naming.mode === 'rename'
              ? 'Rename folder'
              : naming.parentId
                ? `New folder in “${folderName(naming.parentId)}”`
                : 'New folder'
          }
          initial={naming.mode === 'rename' ? naming.folder.name : ''}
          submitLabel={naming.mode === 'rename' ? 'Save' : 'Create'}
          onClose={() => setNaming(null)}
          onSubmit={async (name) => {
            if (naming.mode === 'rename') await api.renameFolder(naming.folder.id, name);
            else {
              const created = await api.createFolder(name, naming.parentId);
              if (naming.parentId) setExpanded((prev) => new Set(prev).add(naming.parentId!));
              toast.push(`Created “${created.name}”`, 'success');
            }
            setNaming(null);
            folders.refetch();
          }}
        />
      ) : null}
      {sharing !== null ? (
        <ShareWithDemoAdmins type="media" id={sharing.id} name={sharing.name} onClose={() => setSharing(null)} />
      ) : null}
    </Page>
  );
}

function folderSummary(f: MediaFolder): string {
  const parts = [`${f.mediaCount} file${f.mediaCount === 1 ? '' : 's'}`];
  if (f.childrenCount > 0) parts.push(`${f.childrenCount} folder${f.childrenCount === 1 ? '' : 's'}`);
  return parts.join(' · ');
}

/**
 * Images use the server's small WebP thumbnail. Videos get a placeholder,
 * not a `<video>`: every one of those would fetch through PHP as soon as the
 * page opened. The preview plays the real file.
 */
function MediaThumb({ item, small }: { item: MediaItem; small?: boolean }): ReactNode {
  const kind = item.mime?.split('/')[0];
  return (
    <div className={small ? 'explorer-thumb small' : 'explorer-thumb'}>
      {kind === 'image' ? (
        <img src={api.assetUrl(item.thumbUrl ?? item.url)} alt="" loading="lazy" decoding="async" draggable={false} />
      ) : (
        <span className="faint mono small" style={kind === 'video' && !small ? { fontSize: 28 } : undefined}>
          {kind === 'video' ? '▶' : (item.mime?.split('/')[1] ?? 'file')}
        </span>
      )}
      {kind === 'video' && !small ? (
        <span className="explorer-badge">{item.duration ? `${Math.round(item.duration)}s` : 'video'}</span>
      ) : null}
    </div>
  );
}

/**
 * The end of the loaded list: loads the next page when it scrolls near view,
 * or when clicked. It re-observes after each load, so a page too short to
 * fill the pane keeps loading.
 */
function LoadMore({ loading, onLoad }: { loading: boolean; onLoad: () => void }): ReactNode {
  const ref = useRef<HTMLButtonElement>(null);
  const onLoadRef = useRef(onLoad);
  onLoadRef.current = onLoad;

  useEffect(() => {
    const el = ref.current;
    if (!el || loading || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) onLoadRef.current();
    }, { rootMargin: '600px 0px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, [loading]);

  return (
    <div className="row" style={{ justifyContent: 'center', marginTop: 14 }}>
      <button ref={ref} type="button" className="btn small ghost" disabled={loading} onClick={onLoad}>
        {loading ? 'Loading…' : 'Load more'}
      </button>
    </div>
  );
}

function PreviewModal({
  item,
  folder,
  onClose,
  onCopy,
}: {
  item: MediaItem;
  folder: string;
  onClose: () => void;
  onCopy: () => void;
}): ReactNode {
  const kind = item.mime?.split('/')[0];
  const [videoRatio, setVideoRatio] = useState<number | null>(null);
  const ratio = videoRatio ?? (item.width && item.height ? item.width / item.height : null);
  return (
    <Modal title={item.originalName ?? '(unnamed)'} onClose={onClose} wide>
      <div className="explorer-preview">
        {kind === 'image' ? (
          <img src={api.assetUrl(item.url)} alt={item.originalName ?? item.sha256} />
        ) : kind === 'video' ? (
          <video
            src={api.assetUrl(item.url)}
            controls
            autoPlay
            muted
            playsInline
            // Locked to the video's own shape: the stored dimensions up front (so the box doesn't jump
            // from the browser's 2:1 default), then the decoded size once metadata loads.
            onLoadedMetadata={(e) => {
              const { videoWidth, videoHeight } = e.currentTarget;
              if (videoWidth && videoHeight) setVideoRatio(videoWidth / videoHeight);
            }}
            style={ratio ? { aspectRatio: String(ratio), width: `min(100%, calc(60vh * ${ratio}))` } : undefined}
          />
        ) : (
          <span className="faint mono">{item.mime ?? 'file'}</span>
        )}
      </div>
      <dl className="explorer-facts">
        <dt>Folder</dt>
        <dd>{folder}</dd>
        <dt>Type</dt>
        <dd className="mono small">{item.mime ?? '—'}</dd>
        <dt>Size</dt>
        <dd>{fileSize(item.size)}</dd>
        {item.width && item.height ? (
          <>
            <dt>Dimensions</dt>
            <dd>
              {item.width}×{item.height}
            </dd>
          </>
        ) : null}
        {item.duration ? (
          <>
            <dt>Duration</dt>
            <dd>{item.duration}s</dd>
          </>
        ) : null}
        <dt>SHA-256</dt>
        <dd>
          <button
            type="button"
            className="btn ghost small mono explorer-digest"
            title="Copy digest"
            onClick={() => void navigator.clipboard?.writeText(item.sha256).then(onCopy)}
          >
            {item.sha256}
          </button>
        </dd>
      </dl>
      <div className="row between" style={{ marginTop: 16 }}>
        <a className="btn" href={api.assetUrl(item.url)} download={item.originalName ?? item.sha256}>
          Download
        </a>
        <button className="btn primary" onClick={onClose}>
          Close
        </button>
      </div>
    </Modal>
  );
}

function MoveModal({
  title,
  folders,
  exclude,
  initial,
  onClose,
  onMove,
}: {
  title: string;
  folders: MediaFolder[];
  /** A folder being moved: it and its subtree can't be the destination. */
  exclude?: string;
  initial: string | null;
  onClose: () => void;
  onMove: (target: string | null) => void;
}): ReactNode {
  const [target, setTarget] = useState<string | null>(initial);
  const byParent = childrenByParent(folders);

  function level(parentId: string | null, depth: number): ReactNode[] {
    return (byParent.get(parentId) ?? []).flatMap((f) => {
      if (exclude && isWithin(folders, f.id, exclude)) return [];
      return [
        <label key={f.id} className={`explorer-pick${target === f.id ? ' active' : ''}`} style={{ paddingLeft: 10 + depth * 16 }}>
          <input type="radio" name="move-target" checked={target === f.id} onChange={() => setTarget(f.id)} />
          <FolderIcon />
          <span className="ellipsis">{f.name}</span>
        </label>,
        ...level(f.id, depth + 1),
      ];
    });
  }

  return (
    <Modal title={title} onClose={onClose}>
      <div className="explorer-picker">
        <label className={`explorer-pick${target === null ? ' active' : ''}`}>
          <input type="radio" name="move-target" checked={target === null} onChange={() => setTarget(null)} />
          <HomeIcon />
          <span>Library (unfiled)</span>
        </label>
        {level(null, 1)}
      </div>
      <div className="row between" style={{ marginTop: 16 }}>
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
        <button className="btn primary" disabled={target === initial} onClick={() => onMove(target)}>
          Move here
        </button>
      </div>
    </Modal>
  );
}

function NameModal({
  title,
  initial,
  submitLabel,
  onClose,
  onSubmit,
}: {
  title: string;
  initial: string;
  submitLabel: string;
  onClose: () => void;
  onSubmit: (name: string) => Promise<void>;
}): ReactNode {
  const [name, setName] = useState(initial);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const valid = name.trim() !== '' && name.trim() !== initial;

  async function submit(): Promise<void> {
    setBusy(true);
    try {
      await onSubmit(name.trim());
    } catch (err) {
      toast.error(err);
      setBusy(false);
    }
  }

  return (
    <Modal title={title} onClose={onClose}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid && !busy) void submit();
        }}
      >
        <label className="field">
          Name
          <input value={name} onChange={(e) => setName(e.target.value)} autoFocus maxLength={255} />
        </label>
        <div className="row between">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn primary" disabled={busy || !valid}>
            {busy ? 'Saving…' : submitLabel}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function FolderIcon({ open, size = 16 }: { open?: boolean; size?: number }): ReactNode {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" className="explorer-icon folder-icon">
      <path
        d={open ? 'M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v1H7.5a2 2 0 0 0-1.9 1.4L3 19V7z M6.2 12h15.6l-2.4 7H3.8z' : 'M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z'}
        fill="currentColor"
      />
    </svg>
  );
}

function HomeIcon(): ReactNode {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" aria-hidden="true" className="explorer-icon">
      <path d="M3 11 12 4l9 7v8a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1v-8z" fill="currentColor" />
    </svg>
  );
}

function StackIcon(): ReactNode {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" aria-hidden="true" className="explorer-icon">
      <path d="M4 5h16v4H4zM4 11h16v4H4zM4 17h16v3H4z" fill="currentColor" />
    </svg>
  );
}
